import { Box, Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';

type TurnstileApi = {
    render: (
        container: HTMLElement,
        options: {
            sitekey: string;
            action: string;
            language: string;
            theme: 'auto';
            callback: (token: string) => void;
            'expired-callback': () => void;
            'error-callback': () => void;
        }
    ) => string;
    remove: (widgetId: string) => void;
};

declare global {
    interface Window {
        turnstile?: TurnstileApi;
    }
}

const SCRIPT_ID = 'cloudflare-turnstile-script';
let scriptPromise: Promise<void> | null = null;

function loadTurnstileScript() {
    if (window.turnstile) {
        return Promise.resolve();
    }

    if (scriptPromise) {
        return scriptPromise;
    }

    scriptPromise = new Promise<void>((resolve, reject) => {
        const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
        const script = existing || document.createElement('script');

        const handleLoad = () => resolve();
        const handleError = () => reject(new Error('Turnstile script failed to load'));

        script.addEventListener('load', handleLoad, { once: true });
        script.addEventListener('error', handleError, { once: true });

        if (!existing) {
            script.id = SCRIPT_ID;
            script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
            script.async = true;
            script.defer = true;
            document.head.appendChild(script);
        }
    }).catch((error) => {
        scriptPromise = null;
        throw error;
    });

    return scriptPromise;
}

type TurnstileWidgetProps = {
    siteKey: string;
    onTokenChange: (token: string) => void;
};

export default function TurnstileWidget({ siteKey, onTokenChange }: TurnstileWidgetProps) {
    const containerRef = useRef<HTMLDivElement>(null);
    const [loadFailed, setLoadFailed] = useState(false);

    useEffect(() => {
        let disposed = false;
        let widgetId: string | undefined;

        loadTurnstileScript()
            .then(() => {
                if (disposed || !containerRef.current || !window.turnstile) {
                    return;
                }
                widgetId = window.turnstile.render(containerRef.current, {
                    sitekey: siteKey,
                    action: 'register',
                    language: 'ru',
                    theme: 'auto',
                    callback: onTokenChange,
                    'expired-callback': () => onTokenChange(''),
                    'error-callback': () => onTokenChange(''),
                });
            })
            .catch(() => {
                if (!disposed) {
                    setLoadFailed(true);
                    onTokenChange('');
                }
            });

        return () => {
            disposed = true;
            if (widgetId && window.turnstile) {
                window.turnstile.remove(widgetId);
            }
        };
    }, [onTokenChange, siteKey]);

    if (loadFailed) {
        return (
            <Typography
                variant="body2"
                color="error"
            >
                Проверка не загрузилась. Обновите страницу.
            </Typography>
        );
    }

    return (
        <Box
            ref={containerRef}
            minHeight={65}
        />
    );
}
